/**
 * Wave A — `useCanAccess(permission, resourceId?)`.
 *
 * Thin wrapper over `usePermissions().has(...)` that returns `false` while the
 * permissions cache is still warming up (fail-closed UI).
 *
 * @module shared/hooks/useCanAccess
 */
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { isPlatformAdmin } from '../rbac/roles';

function checkIsPlatformAdmin(user: unknown): boolean {
  if (!user || typeof user !== 'object') return false;
  const u = user as Record<string, unknown>;
  const userRoleStrings: string[] = [
    ...((u.roles as string[] | undefined) ?? []),
    u.role as string | undefined,
    (u.accountRole as string | undefined) ?? (u.account_role as string | undefined),
  ].filter(Boolean) as string[];

  return isPlatformAdmin(userRoleStrings);
}

/**
 * Returns `true` iff the user is currently allowed to perform `permission` on
 * `resourceId` (or anywhere when `resourceId` is omitted).
 *
 * Platform admins (master_admin / super_admin) have unrestricted platform-wide access.
 * Returns `false` while the permissions query is loading for other users.
 */
export function useCanAccess(permission: string, resourceId?: string): boolean {
  let user: unknown = useAuth(true)?.user;

  if (!user && typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem('user');
      if (stored) {
        user = JSON.parse(stored);
      }
    } catch {
      // ignore
    }
  }

  const platformAdmin = checkIsPlatformAdmin(user);
  const { has, loading } = usePermissions({ enabled: !platformAdmin });
  if (platformAdmin) {
    return true;
  }

  if (loading) return false;
  return has(permission, resourceId);
}
